if (!customElements.get('so-preorder-pdp')) {
  // Public Storefront API token (safe to embed client-side — unauthenticated read scopes only:
  // product_listings, product_inventory, bundles). Used ONLY to check a native fixed bundle's
  // component stock, which Liquid can't see on the product page. On DTM-customapp, 2026-07-22.
  const SO_SF_TOKEN = 'abb399763a0d57d88b7391d78cd31407';
  const SO_SF_URL = '/api/2024-07/graphql.json'; // same-origin — works on any storefront domain

  // P0-2B: button totals read "R957" / "R1,190" (no decimals when whole), else "R95.20".
  const soRand = (cents) => {
    const whole = Math.round(cents) % 100 === 0;
    const d = whole ? 0 : 2;
    return 'R' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
  };

  class SoPreorderPdp extends HTMLElement {
    connectedCallback() {
      // P0-2B: resolve the form first and read THIS form's data element, so a quick-add copy on a
      // product page (two data elements in the document) never reads the main product's variants.
      this.form = this.closest('form[action*="/cart/add"]') || document.querySelector('product-form form');
      const dataEl = (this.form && this.form.querySelector('[data-so-preorder-variants]')) || document.querySelector('[data-so-preorder-variants]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      // 2026-10-13: variants past the first 250 (see variant-picker.js loadMoreVariants). Rows are
      // matched by variant id only, so another product's rows here can never be picked up.
      this.mergeMoreVariants = () => {
        (window.soMoreVariantRows || []).forEach((r) => {
          if (!this.variants.some((x) => x.id === r.id)) this.variants.push(r);
        });
      };
      this.mergeMoreVariants();
      // Bundles are NOT pre-orderable (product decision). A bundle variant is buyable only if EVERY
      // component is in stock; if any component is out of stock the bundle is SOLD OUT (not buyable,
      // not pre-order) and we surface the back-in-stock form. Liquid can't see component stock on the
      // PDP, so for is_bundle products we check the selected variant's components live via Storefront.
      this.isBundle = !!dataEl && dataEl.dataset.soIsBundle === 'true';
      this.bundleOut = {}; // variantId -> boolean (undefined = not fetched yet)
      // 2026-10-13 (Charl): bundles CAN pre-order. With the component stock the page carries
      // (so-preorder-data, data-so-bundle-parts) a bundle variant is in stock, pre-order (every part
      // at zero is continue-selling and none of those needs a full pack) or sold out. Without that
      // data the Storefront check below decides, and the bundle never pre-orders.
      this.bundleParts = null;
      const partsEl = this.form && this.form.querySelector('[data-so-bundle-parts]');
      if (this.isBundle && partsEl) {
        try { this.bundleParts = JSON.parse(partsEl.textContent); } catch (e) { this.bundleParts = null; }
      }
      this.bundleKnown = {}; // F2 (R6): variantId -> true once the component check has ANSWERED (never on failure)
      this.bundleLive = {}; // variantId -> {state, pre:[names], out:[names]} from the live component check
      this.bundleFailed = {}; // variantId -> true when the check failed (the reserved line then collapses)
      this.badge = this.querySelector('.so-preorder-badge');
      // P0-2B: the pack note is moved out of the badge to sit directly above the buy button (where
      // the pack total is). On a reconnect it is already there, so look for it in the form too.
      this.packMsg = this.querySelector('.so-preorder-packmin') || (this.form && this.form.querySelector('.so-preorder-packmin'));
      this.addBtn = this.form ? this.form.querySelector('[name="add"]') : null;
      const atcRow = this.form && this.form.querySelector('.product-info__add-to-cart');
      if (this.packMsg && atcRow && atcRow.parentNode && this.packMsg.nextElementSibling !== atcRow) {
        atcRow.parentNode.insertBefore(this.packMsg, atcRow);
      }
      // Scope to the product section OR the quick-add drawer (this component is copied into the
      // quick-add slide-up, where there's no .shopify-section wrapper).
      this.section = this.closest('.shopify-section, quick-add-drawer') || document;
      this.signup = this.section.querySelector('[data-so-bundle-signup]'); // notify-me form (bundles only)
      // "How pre-orders work" = the shared .so-dsheet--info sheet (so-delivery-sheet.css + window.soSheetSwipe).
      this.modal = this.querySelector('.so-preorder-sheet');
      this.openBtn = this.querySelector('[data-so-open]');
      this._onKey = (e) => {
        if (e.key === 'Escape') { this.closeModal(); return; }
        if (e.key === 'Tab') this.trapTab(e);
      };
      if (this.openBtn) this.openBtn.addEventListener('click', () => this.openModal());
      if (this.modal) {
        this.modal.querySelectorAll('[data-so-close]').forEach((el) => el.addEventListener('click', () => this.closeModal()));
      }
      // F2 (R10): what the cart already holds, for the quantity-aware stock line. Read from the cart
      // drawer's DOM once here, then from the cart the add / change events carry (no extra request).
      this._cartCounts = SoPreorderPdp.drawerCounts();
      this._onCart = (e) => this.onCartEvent(e);
      ['on:cart:add', 'on:line-item:change'].forEach((ev) => document.addEventListener(ev, this._onCart));
      // Adds from Frequently bought together, the cart add-ons, the cart "Change" swap and your-picks fire
      // none of those events, but every one of them re-renders the cart drawer. Watch the drawer (child
      // changes only, debounced) and re-count when its lines change, so "In your cart" can't go stale.
      const cd = document.querySelector('cart-drawer');
      if (cd && window.MutationObserver) {
        this._cartMo = new MutationObserver(() => {
          if (this._cartMoTimer) clearTimeout(this._cartMoTimer);
          this._cartMoTimer = setTimeout(() => {
            this._cartMoTimer = null;
            const m = SoPreorderPdp.drawerCounts();
            if (SoPreorderPdp.sameCounts(m, this._cartCounts)) return;
            this._cartCounts = m;
            this.paintStock(this.currentVariantId(), true);
          }, 150);
        });
        this._cartMo.observe(cd, { childList: true, subtree: true });
      }

      // Initial paint from the currently selected variant id.
      this._onVariant = () => this.update(this.currentVariantId());
      this.update(this.currentVariantId());
      // React to variant changes. Read the canonical hidden [name="id"] input (source of truth
      // the form submits) rather than trust the event detail shape.
      this.section.addEventListener('on:variant:change', this._onVariant);
      // 2026-10-13: products with more than 250 variants. variant-picker.js appends the rest to
      // this form's data element and announces it; re-read so a variant past 250 is known here.
      this._onMoreVariants = () => {
        try { if (dataEl) this.variants = JSON.parse(dataEl.textContent); } catch (e) { /* keep what we have */ }
        this.mergeMoreVariants();
        this._onVariant();
      };
      document.addEventListener('so:variants:extended', this._onMoreVariants);

      // P0-2B: the label carries the total, so it must follow the quantity and survive anything that
      // rewrites the button. Every listener is scoped to THIS form / button (the quick-add drawer
      // renders a second instance) and removed in disconnectedCallback.
      //  - quantity: delegated on the form, so the +/− stepper (dispatches `change`) and typing
      //    (`input`) both count, even if the quantity input is re-rendered.
      //  - after add: product-form.js fires on:cart:add / on:line-item:change on <product-form>.
      //  - guard: a MutationObserver re-asserts the label when another script (variant-picker's
      //    deferred init, a theme reset) writes the button text. refreshLabel() only writes when
      //    the text differs, so our own write does not loop.
      this._onLabel = () => this.refreshLabel();
      this._onQty = (e) => {
        if (!e.target || e.target.name !== 'quantity') return;
        // On commit (`change`), a typed pack quantity is rounded up to whole packs so the form submits
        // exactly what the label says (a step-misaligned value would also fail validation). Writing
        // the same value again is skipped, so the re-dispatched change does not loop.
        if (e.type === 'change') { this.normalizeQty(); this.clampQty(); }
        this.refreshLabel();
        // F2 Q1: the line compares stepper + cart with stock. Typing (input) only repaints; the clamp is on change.
        this.paintStock(this.currentVariantId());
      };
      if (this.form) {
        this.form.addEventListener('change', this._onQty);
        this.form.addEventListener('input', this._onQty);
      }
      this.productForm = this.closest('product-form');
      if (this.productForm) {
        ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((ev) => this.productForm.addEventListener(ev, this._onLabel));
      }
      if (this.addBtn && window.MutationObserver) {
        this._labelMo = new MutationObserver(this._onLabel);
        this._labelMo.observe(this.addBtn, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      }

      // DEEP-LINK REPAINT (2026-09-22). Landing directly on
      // ?variant=<pre-order variant> left the buy button reading "Add to cart"
      // while the amber pre-order panel right below it said pre-order.
      //
      // The SERVER renders the button correctly — fetching the same URL
      // returns "Pre-order" in the HTML. A theme init pass racing this
      // component then resets the label from data-add-to-cart-text, and on a
      // deep link no on:variant:change follows to correct it. The only way a
      // customer got the right label was to touch the variant picker.
      //
      // It matters because deep links are the norm, not the edge case: so-mail
      // campaign links carry ?variant=, the back-in-stock alert email links
      // straight at the restocked variant, and Google's merchant listings are
      // per-variant. Someone clicking "it's back" would see "Add to cart",
      // buy, and be surprised by a 5–10 day wait.
      //
      // Re-asserting a few times after the theme settles is enough: once a
      // repaint lands nothing resets it again (verified with a
      // MutationObserver over 1.5s). So no polling and no observer to leak —
      // just an rAF, a short timeout, and `load` if it has not fired yet.
      // update() is a pure DOM write, so extra calls are harmless no-ops.
      this._repaintFrame = requestAnimationFrame(this._onVariant);
      this._repaintTimer = setTimeout(this._onVariant, 400);
      if (document.readyState !== 'complete') {
        this._onLoad = this._onVariant;
        window.addEventListener('load', this._onLoad, { once: true });
      }
    }
    disconnectedCallback() {
      // The quick-add drawer replaces its contents on each open — remove listeners so they don't pile up.
      if (this.section && this._onVariant) this.section.removeEventListener('on:variant:change', this._onVariant);
      if (this._onMoreVariants) document.removeEventListener('so:variants:extended', this._onMoreVariants);
      // Same reason: the drawer connects/disconnects this component repeatedly,
      // so a pending repaint must not fire against a detached instance.
      if (this._repaintFrame) cancelAnimationFrame(this._repaintFrame);
      if (this._repaintTimer) clearTimeout(this._repaintTimer);
      if (this._onLoad) window.removeEventListener('load', this._onLoad);
      if (this._onCart) ['on:cart:add', 'on:line-item:change'].forEach((ev) => document.removeEventListener(ev, this._onCart));
      if (this._cartTimer) clearTimeout(this._cartTimer);
      if (this._cartMo) { this._cartMo.disconnect(); this._cartMo = null; }
      if (this._cartMoTimer) clearTimeout(this._cartMoTimer);
      // P0-2B: label listeners + observer.
      if (this.form && this._onQty) {
        this.form.removeEventListener('change', this._onQty);
        this.form.removeEventListener('input', this._onQty);
      }
      if (this.productForm && this._onLabel) {
        ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((ev) => this.productForm.removeEventListener(ev, this._onLabel));
      }
      if (this._labelMo) this._labelMo.disconnect();
      document.removeEventListener('keydown', this._onKey);
      // Removed while its sheet is open (quick-add drawer re-render): give the page its scroll back.
      if (this._saved) {
        document.documentElement.style.overflow = this._saved.html;
        document.body.style.overflow = this._saved.body;
        this._saved = null;
      }
      this._modalOpen = false;
      clearTimeout(this._modalCloseT);
      // If we portaled the modal to <body>, take it with us so it doesn't orphan.
      if (this._modalPortaled && this.modal && this.modal.parentElement === document.body) this.modal.remove();
    }
    // Same open/close path as the PDP trust cards (so-pdp-info.js): .is-open drives the shell's slide (phone) /
    // fade (>= 768px); window.soSheetSwipe adds swipe-down-to-close on phones through closeModal(), like ✕,
    // the backdrop and Escape.
    openModal() {
      const m = this.modal;
      if (!m || this._modalOpen) return;
      // Portal the modal to <body> so nested stacking contexts (the gallery's carousel arrows, the
      // column divider) can't render on top of it — z-index alone can't escape an ancestor context.
      if (m.parentElement !== document.body) {
        document.body.appendChild(m);
        this._modalPortaled = true;
      }
      clearTimeout(this._modalCloseT);
      m.classList.remove('is-closing');
      m.hidden = false;
      this._modalOpen = true;
      if (!this._saved) this._saved = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      const panel = m.querySelector('.so-dsheet__panel');
      if (window.soSheetSwipe && panel) {
        window.soSheetSwipe.attach(panel, {
          handle: '.so-dsheet__grab, .so-dsheet__head', scroller: '.so-dsheet__body',
          backdrop: m.querySelector('.so-dsheet__backdrop'), onClose: () => this.closeModal()
        });
      }
      void m.offsetWidth;                     // reflow, then slide in
      m.classList.add('is-open');
      const t = m.querySelector('.so-dsheet__title');
      if (t) t.focus({ preventScroll: true });
    }
    closeModal() {
      const m = this.modal;
      if (!m || !this._modalOpen) return;
      this._modalOpen = false;
      m.classList.remove('is-open');
      m.classList.add('is-closing');
      this._modalCloseT = setTimeout(() => { m.hidden = true; m.classList.remove('is-closing'); }, 260);
      if (this._saved) {
        document.documentElement.style.overflow = this._saved.html;
        document.body.style.overflow = this._saved.body;
        this._saved = null;
      }
      document.removeEventListener('keydown', this._onKey);
      if (this.openBtn && this.openBtn.isConnected) this.openBtn.focus({ preventScroll: true });
    }
    // Keep Tab focus inside the open sheet.
    trapTab(e) {
      if (!this._modalOpen || !this.modal) return;
      const f = Array.from(this.modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1], a = document.activeElement, inList = f.includes(a);
      if (e.shiftKey) {
        if (!inList || a === first) { last.focus(); e.preventDefault(); }
      } else if (!inList || a === last) { first.focus(); e.preventDefault(); }
    }
    currentVariantId() {
      const input = this.form && this.form.querySelector('[name="id"]');
      return input ? Number(input.value) : null;
    }
    // Individual (non-bundle) products: the canonical pre-order rule. Bundles: see bundleStateFor().
    isPreorder(id) {
      if (this.isBundle) return this.bundleStateFor(id) === 'pre';
      return SoPreorderPdp.preRaw(this.variants.find((x) => x.id === Number(id)));
    }
    // P0-2B: an UNTRACKED variant reports inventory_quantity 0 but is always buyable, so it is a plain
    // add-to-cart, never a pre-order (see the so-preorder-data header).
    static preRaw(v) {
      return !!v && v.tracked !== false && v.inventory_policy === 'continue' && v.inventory_quantity <= 0;
    }
    // Pack minimum (pre-order-only, per-variant). Never applies to bundles.
    packInfo(id) {
      const v = this.variants.find((x) => x.id === Number(id));
      if (!v || this.isBundle) return { enforce: false, size: 1 };
      const size = Number(v.pack_size) || 1;
      const enforce = v.full_pack === true && size >= 2 && SoPreorderPdp.preRaw(v);
      return { enforce, size };
    }
    // Bundle state from the component stock on the page. parts: [{single, v:{title:[continue, qty,
    // fullPack, tracked]}}]; opts: the bundle variant's option values. A part's variant is the one whose
    // title is one of those values (a one-variant part always counts). 'in' = no part at zero;
    // 'pre' = every part at zero is continue-selling and none needs a full pack; 'out' = otherwise.
    // Quantities per bundle are not known here — the live check (liveBundle) and the cart check them.
    // null when a multi-variant part matches none of the option values: unknown, never 'in' by default.
    // Shared with so-variant-drawer.js (customElements.get('so-preorder-pdp').bundleState).
    static bundleState(parts, opts) {
      let short = false, block = false, unknown = false;
      (parts || []).forEach((p) => {
        if (!p || !p.v) return;
        let e = null;
        if (p.single) { const k = Object.keys(p.v)[0]; e = k != null ? p.v[k] : null; }
        else (opts || []).some((o) => { if (Object.prototype.hasOwnProperty.call(p.v, o)) { e = p.v[o]; return true; } return false; });
        if (!e) { if (!p.single) unknown = true; return; }
        if (!e[3] || e[1] > 0) return;
        short = true;
        if (!e[0] || e[2]) block = true;
      });
      if (short && block) return 'out';
      if (unknown) return null;
      return short ? 'pre' : 'in';
    }
    // Names of the parts at zero for these option values: pre = continue-selling, out = blocking
    // (stop-selling or full pack). A multi-variant part is named by the option value it matched
    // ("#7 South Pole", "Ranger Green"); a one-variant part by its short name ("n").
    static bundleShort(parts, opts) {
      const pre = [], out = [];
      (parts || []).forEach((p) => {
        if (!p || !p.v) return;
        let e = null, name = p.n || '';
        if (p.single) { const k = Object.keys(p.v)[0]; e = k != null ? p.v[k] : null; }
        else (opts || []).some((o) => { if (Object.prototype.hasOwnProperty.call(p.v, o)) { e = p.v[o]; name = o; return true; } return false; });
        if (!e || !e[3] || e[1] > 0) return;
        ((!e[0] || e[2]) ? out : pre).push(name);
      });
      return { pre, out };
    }
    bundlePartLabel(id, state) {
      const live = this.bundleLive[id];
      if (live) return (state === 'pre' ? live.pre : live.out).filter(Boolean).join(' & ');
      if (!this.bundleParts) return '';
      const v = this.variants.find((x) => x.id === Number(id));
      if (!v) return '';
      const s = SoPreorderPdp.bundleShort(this.bundleParts, String(v.title || '').split(' / '));
      return (state === 'pre' ? s.pre : s.out).filter(Boolean).join(' & ');
    }
    // The live check (ensureBundleData) wins once it has answered; until then the page's parts data
    // gives a first answer. null = neither is known yet.
    bundleStateFor(id) {
      if (!this.isBundle) return null;
      const v = this.variants.find((x) => x.id === Number(id));
      if (!v) return null;
      const live = this.bundleLive[id];
      let st = live ? live.state : null;
      if (!st && this.bundleParts) {
        st = SoPreorderPdp.bundleState(this.bundleParts, String(v.title || '').split(' / '));
      }
      // A bundle variant Shopify will not sell (stop-selling at zero) can never be a pre-order.
      if (st === 'pre' && !SoPreorderPdp.availableNow(v)) return 'out';
      return st;
    }
    // Live component check (Storefront), once per variant. It is the authority for WHICH parts are
    // short: real available stock against the per-bundle quantity (a bundle with 2 pods needs 2), even
    // when the page HTML is cached or a part does not match an option value. The page's parts data
    // only says whether a short part can be pre-ordered (continue-selling, not a full pack); a short
    // part it does not know is treated as sold out — never promise what we cannot check.
    static liveBundle(nodes, parts) {
      const pre = [], out = [];
      (nodes || []).forEach((n) => {
        const pv = n && n.productVariant;
        if (!pv) return;
        const need = Math.max(1, Number(n.quantity) || 1);
        const qa = pv.quantityAvailable;
        const short = qa != null ? Number(qa) < need : (pv.currentlyNotInStock === true || pv.availableForSale === false);
        if (!short) return;
        const pid = pv.product && pv.product.id ? String(pv.product.id).split('/').pop() : null;
        const part = (parts || []).find((p) => p && p.id != null && String(p.id) === pid);
        const e = part && part.v ? (part.single ? part.v[Object.keys(part.v)[0]] : part.v[pv.title]) : null;
        const name = part && part.single ? (part.n || '') : (pv.title && pv.title !== 'Default Title' ? pv.title : (part && part.n) || '');
        const canPre = e ? (!!e[0] && !e[2]) : false;
        (canPre && pv.availableForSale !== false ? pre : out).push(name);
      });
      return { state: out.length ? 'out' : pre.length ? 'pre' : 'in', pre, out };
    }
    ensureBundleData(id) {
      if (!this.isBundle || id == null) return;
      // First answer from the page's parts data (instant), then the live check confirms or corrects it.
      if (!this.bundleKnown[id]) {
        const local = this.bundleStateFor(id);
        if (local) { this.bundleOut[id] = local === 'out'; this.bundleKnown[id] = true; }
      }
      if (this.bundleLive[id] !== undefined) return; // checked or in-flight
      this.bundleLive[id] = null; // in-flight marker
      if (this.bundleOut[id] === undefined) this.bundleOut[id] = false; // safe default = buyable (the cart guards)
      const query = 'query($id: ID!) { node(id: $id) { ... on ProductVariant { components(first: 30) { nodes { quantity productVariant { title availableForSale currentlyNotInStock quantityAvailable product { id } } } } } } }';
      fetch(SO_SF_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': SO_SF_TOKEN },
        body: JSON.stringify({ query, variables: { id: 'gid://shopify/ProductVariant/' + id } }),
      })
        .then((r) => r.json())
        .then((j) => {
          const node = j && j.data && j.data.node;
          if (!node || !node.components) throw new Error('no answer'); // R6: never guess
          const res = SoPreorderPdp.liveBundle(node.components.nodes || [], this.bundleParts);
          this.bundleLive[id] = res;
          const st = this.bundleStateFor(id);
          this.bundleOut[id] = st === 'out';
          this.bundleKnown[id] = true;
          if (this.currentVariantId() === Number(id)) {
            this.update(id);
            // so-stock-alert follows the bundle state; tell it the answer changed.
            this.section.dispatchEvent(new CustomEvent('so:bundle:state', { detail: { id: Number(id) } }));
          }
        })
        .catch(() => {
          // Keep the page-data answer if there is one; otherwise leave buyable (the cart-side guard is
          // the real-time backstop) and drop the reserved stock line.
          if (!this.bundleKnown[id]) {
            this.bundleFailed[id] = true;
            if (this.currentVariantId() === Number(id)) this.paintStock(id);
          }
        });
    }
    update(id) {
      if (this.isBundle) return this.updateBundle(id);
      const pre = this.isPreorder(id);
      if (this.badge) this.badge.hidden = !pre;
      // Button label: set by refreshLabel() at the end, once the quantity is final (P0-2B).
      const dyn = this.section.querySelector('.shopify-payment-button');
      if (dyn) dyn.style.display = pre ? 'none' : '';
      // F2: the stock line shows every state (pre-order included); only the theme's backorder text stays hidden.
      // The line is painted at the end, once the quantity is final (it compares stepper + cart with stock).
      const bo = this.section.querySelector('.product-backorder');
      if (bo) bo.style.display = pre ? 'none' : '';
      // Pack minimum: floor + step the quantity selector, and show the packs message.
      const pack = this.packInfo(id);
      const qty = this.form && this.form.querySelector('[name="quantity"]');
      if (qty) {
        // F2 Q1: the previous variant's cap must go BEFORE pack rounding (a cap of 2 under a pack of 5 would
        // otherwise fight the rounding). applyCap() sets the new variant's cap in paintStock() below.
        qty.removeAttribute('max');
        qty.min = pack.enforce ? String(pack.size) : '1';
        qty.step = pack.enforce ? String(pack.size) : '1';
        const cur = Number(qty.value) || 1;
        let next = cur;
        if (pack.enforce) {
          // Round UP to a whole pack (a step-misaligned value would also block the submit).
          next = Math.max(pack.size, Math.ceil(cur / pack.size) * pack.size);
        } else if (this._packWas) {
          // P0-2B: leaving a pack flavour for a single-unit / in-stock one resets the pack
          // quantity we imposed, so "Add to cart" does not silently carry 10 units.
          next = 1;
        }
        if (next !== cur) {
          qty.value = String(next);
          qty.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this._packWas = pack.enforce;
      if (this.packMsg) {
        this.packMsg.hidden = !pack.enforce;
        if (pack.enforce) this.packMsg.textContent = 'Sold in packs of ' + pack.size + ' — pre-order in multiples of ' + pack.size + '.';
      }
      this.paintStock(id, true);
      this.refreshLabel();
    }
    // P0-2B: the buy-button label with the total for every buyable state.
    //   in stock / untracked      "Add to cart · R957"   (price × qty)
    //   pre-order (single/pack)   "Pre-order · R1,190"   (pack: qty = whole packs, min one pack)
    //   sold out / not selected   null → the theme's own label is left alone
    // No pack count in the label (Charl, 2026-09-25): the "Sold in packs of N" note above the button
    // and the stepper already show it, and the short form fits one line — the sticky bar uses the
    // same label.
    // P0-2B: ONE source of truth for "what the main button adds": the selected variant, its price and
    // the effective quantity (a full-pack pre-order is max(pack, whole packs covering the typed qty)).
    // The buy-button label and the Frequently-bought-together module (so-fbt.js, via buyState() and
    // the `so:buy-state` event) both read it, so their totals cannot disagree.
    buyState() {
      const id = this.currentVariantId();
      const v = this.variants.find((x) => x.id === Number(id));
      const btn = this.addBtn;
      const price = v ? Number(v.price) : NaN;
      const qtyEl = this.form && this.form.querySelector('[name="quantity"]');
      const q = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
      const pack = this.packInfo(id);
      const pre = this.isPreorder(id);
      const qty = pre && pack.enforce ? Math.max(pack.size, Math.ceil(q / pack.size) * pack.size) : q;
      const addable = !!(v && btn && !btn.disabled && !btn.dataset.soDisabled && v.price != null && isFinite(price));
      return { addable, id: v ? v.id : null, title: v && v.title ? v.title : '', price, qty, preorder: pre,
        packSize: pack.enforce ? pack.size : 1, form: this.form };
    }
    normalizeQty() {
      const qtyEl = this.form && this.form.querySelector('[name="quantity"]');
      const st = this.buyState();
      if (qtyEl && st.packSize > 1 && String(st.qty) !== qtyEl.value) {
        qtyEl.value = String(st.qty);
        qtyEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    labelFor(id) {
      const btn = this.addBtn;
      if (!btn || btn.disabled || btn.dataset.soDisabled) return null;
      const st = this.buyState();
      if (st.id == null || st.id !== Number(id)) return null;
      const dflt = btn.getAttribute('data-add-to-cart-text') || 'Add to cart';
      const base = st.preorder ? 'Pre-order' : dflt;
      if (!st.addable) return base;
      return base + ' · ' + soRand(st.price * st.qty);
    }
    refreshLabel() {
      if (!this.addBtn) return;
      // Tell listeners (so-fbt.js) the main buy state may have changed. Cheap; they only re-total.
      try { this.dispatchEvent(new CustomEvent('so:buy-state', { bubbles: true })); } catch (e) { /* old browsers */ }
      const t = this.labelFor(this.currentVariantId());
      // Only write when it differs: our own write re-triggers the observer, which then no-ops.
      if (t && this.addBtn.textContent.trim() !== t) this.addBtn.textContent = t;
    }
    // ── F2: the one stock line (product-inventory > so-stock) follows the selected variant ──────────────
    // Wording and tone come from SO.stock (so-stock-state.js); this only feeds it the variant, the stepper
    // quantity, what the cart already holds and whether a notify form exists here (R5: not in quick-add).
    // Bundles (R6): stored quantities lag their components, so the line waits for the component check and
    // then says "In stock" or "Out of stock" — never a count, never pre-order, never a guess on failure.
    // clamp: also clamp the stepper to the new cap (variant / cart change). Quantity events pass no clamp:
    // _onQty clamps on change itself, and never while typing.
    paintStock(id, clamp) {
      const el = this.section.querySelector('product-inventory so-stock');
      if (!el) return;
      const host = el.closest('product-inventory') || el;
      // F2 M2: if the vocabulary script never loaded, hide the line rather than leave it frozen on
      // the landing variant's text — a missing line is safer than a wrong one.
      if (!window.SO || !SO.stock) { host.hidden = true; return; }
      if (el.hasAttribute('data-dead')) return; // R8: "No longer stocked" is the server's, never repainted
      const notify = !!this.section.querySelector('[data-so-stock-alert]');
      if (this.isBundle) {
        // While the component check runs, keep the line's space (visibility, not display) so nothing
        // below it jumps when the answer lands; if the check fails, collapse it — never guess (R6).
        const known = !!this.bundleKnown[id];
        host.hidden = !known && !!this.bundleFailed[id];
        host.style.visibility = known ? '' : 'hidden';
        if (!known) return;
        const out = this.bundleOut[id] === true;
        const pre = !out && this.isPreorder(id);
        SO.stock.set(el, out ? { tracked: true, policy: 'deny', qty: 0 } : pre ? { tracked: true, policy: 'continue', qty: 0 } : { tracked: false },
          { notify: out && (notify || !!this.signup), want: 0, inCart: 0, part: out ? this.bundlePartLabel(id, 'out') : pre ? this.bundlePartLabel(id, 'pre') : '' });
        return;
      }
      const v = this.variants.find((x) => x.id === Number(id));
      if (!v) return;
      const inCart = this.cartQty(v.id);
      // After a clamp the stepper shows the cap, but the line still answers what was ASKED for ("that's all
      // we have"), until the shopper moves the stepper or the variant changes.
      let want = this.buyState().qty;
      if (this._asked && this._asked.id === v.id && this._asked.at === want) want = Math.max(want, this._asked.want);
      else this._asked = null;
      SO.stock.set(el, v, { notify, want, inCart });
      this.applyCap(v, inCart, clamp);
    }
    // Q1 buy controls for sell-to-zero items: the stepper can't exceed what's left after the cart, and at 0
    // left the button says "In your cart". Only undoes what it set (data-so-disabled="cart").
    applyCap(v, inCart, clamp) {
      const c = SO.stock.classify(v, SO.stock.lowAt());
      const max = SO.stock.maxAddable(c, inCart);
      const qty = this.form && this.form.querySelector('[name="quantity"]');
      if (qty) {
        if (max != null && max >= 1) qty.max = String(max); // never below 1: at 0 the button is disabled instead
        else qty.removeAttribute('max');
      }
      if (clamp) this.clampQty(); // a variant / cart change can lower the cap under the stepper
      const btn = this.addBtn;
      if (!btn) return;
      if (max === 0) {
        btn.dataset.soDisabled = 'cart';
        btn.disabled = true;
        if (btn.textContent.trim() !== 'In your cart') btn.textContent = 'In your cart';
      } else if (btn.dataset.soDisabled === 'cart') {
        delete btn.dataset.soDisabled;
        btn.disabled = !SoPreorderPdp.availableNow(v);
        if (btn.disabled) btn.textContent = (window.theme && theme.strings && theme.strings.noStock) || 'Sold out'; // variant-picker's own sold-out label
        else btn.textContent = btn.getAttribute('data-add-to-cart-text') || 'Add to cart';
        this.refreshLabel();
      }
    }
    // On commit, a typed quantity above the cap is clamped (the re-dispatched change is then a no-op) and the
    // ask is remembered for the line (see paintStock).
    // The cap comes from the CURRENT variant (never a stale max attribute) and never applies to bundles or
    // to pack / pre-order variants (their quantity is owned by the pack rounding).
    clampQty() {
      const qty = this.form && this.form.querySelector('[name="quantity"]');
      if (!qty || this.isBundle || !window.SO || !SO.stock) return;
      const id = this.currentVariantId();
      const v = this.variants.find((x) => x.id === Number(id));
      if (!v || this.isPreorder(id) || this.packInfo(id).enforce) return;
      const max = SO.stock.maxAddable(SO.stock.classify(v, SO.stock.lowAt()), this.cartQty(v.id));
      const cur = Number(qty.value) || 1;
      if (max != null && max >= 1 && cur > max) {
        this._asked = { id: this.currentVariantId(), want: cur, at: max };
        qty.value = String(max);
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    static availableNow(v) {
      return !v || v.tracked === false || v.inventory_quantity > 0 || v.inventory_policy === 'continue';
    }
    cartQty(id) { return (this._cartCounts && this._cartCounts[Number(id)]) || 0; }
    static sameCounts(a, b) {
      const ka = Object.keys(a || {}), kb = Object.keys(b || {});
      return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
    }
    // variantId -> quantity, from the cart drawer rows (not /cart's main-cart, which would double count).
    static drawerCounts() {
      const m = {};
      document.querySelectorAll('cart-drawer .cart-item[data-variant-id]').forEach((row) => {
        const q = row.querySelector('input[name="updates[]"]');
        const id = Number(row.getAttribute('data-variant-id'));
        m[id] = (m[id] || 0) + (q ? parseInt(q.value, 10) || 0 : 0);
      });
      return m;
    }
    onCartEvent(e) {
      const items = e && e.detail && e.detail.cart && e.detail.cart.items;
      if (Array.isArray(items)) {
        const m = {};
        items.forEach((it) => { const id = Number(it.variant_id); m[id] = (m[id] || 0) + (Number(it.quantity) || 0); });
        this._cartCounts = m;
      }
      this.paintStock(this.currentVariantId(), true);
      if (Array.isArray(items)) return;
      // No cart in the event: product-form.js re-renders the drawer ~700 ms after an add; re-read it then.
      if (this._cartTimer) clearTimeout(this._cartTimer);
      this._cartTimer = setTimeout(() => {
        this._cartTimer = null;
        this._cartCounts = SoPreorderPdp.drawerCounts();
        this.paintStock(this.currentVariantId(), true);
      }, 800);
    }
    updateBundle(id) {
      this.ensureBundleData(id);
      const out = this.bundleOut[id] === true;
      const pre = !out && this.isPreorder(id);
      if (this.badge) this.badge.hidden = !pre; // pre-order bundles show the same badge as singles
      if (this.addBtn) {
        if (out) {
          if (!this.addBtn.dataset.soDisabled) this.addBtn.dataset.soDisabled = '1';
          this.addBtn.textContent = 'Sold out';
          this.addBtn.disabled = true;
          this.addBtn.setAttribute('aria-disabled', 'true');
        } else if (this.addBtn.dataset.soDisabled) {
          // Only restore the button if WE disabled it (don't clobber the theme's own states).
          this.addBtn.disabled = false;
          this.addBtn.removeAttribute('aria-disabled');
          this.addBtn.textContent = this.addBtn.getAttribute('data-add-to-cart-text') || 'Add to cart';
          delete this.addBtn.dataset.soDisabled;
        }
      }
      const dyn = this.section.querySelector('.shopify-payment-button');
      if (dyn) dyn.style.display = out || pre ? 'none' : '';
      this.paintStock(id);
      if (this.signup) this.signup.hidden = !out; // notify-me form only when sold out
      this.refreshLabel(); // P0-2B: buyable bundles show the bundle total too (no-op when sold out)
    }
  }
  SoPreorderPdp.formatRand = soRand; // P0-2B: so-fbt.js prints its total in the button's format
  customElements.define('so-preorder-pdp', SoPreorderPdp);
}
